import { Form, Formik, type FormikHelpers } from "formik";
// @ts-expect-error pure js
import { toInitialValuesFromSchema } from "../../models/utils";
import {
  EDL_ITEM_CHILD_VALUE,
  EDL_ITEM_INITIAL_VALUE,
  EDL_ITEM_TEMPLATE_INITIAL_VALUE,
  edlItemChildValidationSchema,
  edlItemTemplateValidationSchema,
  edlItemValidationSchema,
  // @ts-expect-error pure js
} from "../../models/edl";
import EdlItemFormInputs from "./EdlItemFormInputs";
// @ts-expect-error pure js
import useEdlItemsTemplates from "../../hooks/use-edl-items-templates";
// @ts-expect-error pure js
import { ReactSelect } from "../UI/Select";
// @ts-expect-error pure js
import Checkbox from "../UI/Checkbox";
import Callout from "../UI/Callout";
import Button from "../UI/Button";
import ModalDialog, { ModalDialogFooter } from "../UI/ModalDialog";
import FormikSubmitButton from "../UI/FormikSubmitButton";
// @ts-expect-error pure js
import { usePropertiesEdlItem } from "../../hooks/use-edl-items";
import styles from "./EdlItemForm.module.css";

type EdlItemChildValues = {
  nom: string;
  note?: string | number;
  items?: EdlItemChildValues[];
  materiaux: string[];
  degradations: string[];
  is_shared?: boolean;
  template_materiaux?: number | string;
  quantite?: number | string;
  type?: string;
  id?: number;
};

export type EdlItemValues = {
  nom: string;
  note?: string | number;
  items: EdlItemChildValues[];
  materiaux: string[];
  degradations: string[];
  photos?: unknown[];
  template?: string;
  commentaire?: string;
  is_shared: boolean;
  type?: string;
  quantite?: number | string;
  id?: number;
};

type EdlItemTemplate = {
  id: number;
  nom: string;
  items?: EdlItemChildValues[];
};

type TemplateOption = {
  value: number;
  label: string;
};

type EdlItemFormProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  itemToEdit?: EdlItemValues | null;
  onSubmit: (
    values: EdlItemValues,
    helpers: FormikHelpers<EdlItemValues>,
  ) => void | Promise<void>;
  onDelete?: (id: number) => void;
  template?: boolean;
  onDuplicate?: (id: number) => void;
  propertyId?: number | string;
  otherRooms?: EdlItemValues[];
};

export default function EdlItemForm({
  open,
  onOpenChange,
  itemToEdit,
  onSubmit,
  onDelete,
  template,
  onDuplicate,
  propertyId,
  otherRooms = [],
}: EdlItemFormProps) {
  const { edlItemsTemplates } = useEdlItemsTemplates() as {
    edlItemsTemplates: EdlItemTemplate[];
  };
  const { propertiesEdlItem } = usePropertiesEdlItem(
    propertyId,
    itemToEdit?.id,
  ) as { propertiesEdlItem: { nom: string }[] };
  const templateOptions: TemplateOption[] = edlItemsTemplates.map((tmpl) => ({
    value: tmpl.id,
    label: tmpl.nom,
  }));
  let initialValues: EdlItemValues = toInitialValuesFromSchema(
    edlItemValidationSchema,
    itemToEdit,
    EDL_ITEM_INITIAL_VALUE,
  );
  if (template) {
    initialValues = toInitialValuesFromSchema(
      edlItemTemplateValidationSchema,
      itemToEdit,
      EDL_ITEM_TEMPLATE_INITIAL_VALUE,
    );
  }

  return (
    <Formik<EdlItemValues>
      initialValues={initialValues}
      validationSchema={
        template ? edlItemTemplateValidationSchema : edlItemValidationSchema
      }
      enableReinitialize={true}
      onSubmit={onSubmit}
    >
      {({ values, setFieldValue, resetForm, submitCount, isValid }) => {
        function changeTemplate(option: TemplateOption | null) {
          if (!option) {
            resetForm();
            return;
          }
          const edlItemsTemplate = edlItemsTemplates?.find(
            (filtered) => filtered.id === option.value,
          );
          const items =
            edlItemsTemplate?.items?.map((tmpl) => {
              const item = toInitialValuesFromSchema(
                edlItemChildValidationSchema,
                tmpl,
                EDL_ITEM_CHILD_VALUE,
              );
              delete item.id;
              return item;
            }) ?? [];
          setFieldValue("items", items);
          setFieldValue("nom", edlItemsTemplate?.nom ?? "");
        }

        const title =
          values.nom || (itemToEdit ? "Éditer une pièce" : "Ajouter une pièce");

        return (
          <ModalDialog
            open={open}
            size="xl"
            noBackdropClose
            header={title}
            onOpenChange={(nextOpen) => {
              if (!nextOpen) resetForm();
              onOpenChange(nextOpen);
            }}
          >
            <Form className={styles.form}>
              {!itemToEdit && (
                <ReactSelect
                  name="template"
                  options={templateOptions}
                  onChange={changeTemplate}
                  label="Choisissez un modèle de pièce"
                />
              )}

              <Checkbox name="is_shared">
                Cette pièce est partagée avec plusieurs locataires
              </Checkbox>
              {values.is_shared && propertiesEdlItem.length > 0 && (
                <Callout variant="success">
                  Cette pièce est partagée entre les biens suivants :{" "}
                  {propertiesEdlItem.map((property) => property.nom).join(", ")}
                </Callout>
              )}

              <EdlItemFormInputs
                item={values}
                isTemplate={template}
                otherRooms={otherRooms}
              />

              {submitCount > 0 && !isValid && (
                <Callout variant="danger">
                  La pièce ne peut pas être enregistrée : un élément ou un
                  sous-élément est incomplet (nom manquant ou valeur invalide).
                  Dépliez les éléments pour repérer les champs signalés.
                </Callout>
              )}

              <ModalDialogFooter
                start={
                  itemToEdit && (
                    <>
                      {onDuplicate && (
                        <Button
                          type="button"
                          variant="secondary"
                          onClick={() => onDuplicate(itemToEdit.id!)}
                          requiresWrite
                        >
                          Dupliquer
                        </Button>
                      )}
                      {onDelete && (
                        <Button
                          type="button"
                          variant="secondary"
                          onClick={() => onDelete(itemToEdit.id!)}
                          requiresWrite
                        >
                          Supprimer
                        </Button>
                      )}
                    </>
                  )
                }
              >
                <FormikSubmitButton variant="primary" requiresWrite>
                  Enregistrer la pièce
                </FormikSubmitButton>
              </ModalDialogFooter>
            </Form>
          </ModalDialog>
        );
      }}
    </Formik>
  );
}
